import React, { useMemo } from "react";
import tw from "tailwind-styled-components";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  Title
);

const COLORS = {
  indigo: "#4F46E5",
  blue: "#2563EB",
  orange: "#EA580C",
  purple: "#7C3AED",
  green: "#059669",
  red: "#DC2626",
  cyan: "#0891B2",
  slate: "#64748B",
  amber: "#D97706",
};

const baseOptions = {
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: "y",
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: "#111827",
      padding: 10,
      displayColors: false,
    },
  },
  scales: {
    x: {
      beginAtZero: true,
      grid: { color: "#F1F5F9" },
      border: { display: false },
      ticks: { precision: 0, color: "#64748B", font: { size: 10 } },
    },
    y: {
      grid: { display: false },
      border: { display: false },
      ticks: { color: "#64748B", font: { size: 11 } },
    },
  },
};

function createBarData(rows, color, label = "Complaints") {
  return {
    labels: rows.map((item) => item.label),
    datasets: [
      {
        label,
        data: rows.map((item) => item.value),
        backgroundColor: color,
        borderRadius: 5,
        barThickness: 16,
      },
    ],
  };
}

function ChartCard({ title, subtitle, children }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardSubtitle>{subtitle}</CardSubtitle>
      </CardHeader>
      <ChartBody>{children}</ChartBody>
    </Card>
  );
}

function EmptyState({ message = "No data available" }) {
  return (
    <EmptyWrap>
      <span>{message}</span>
    </EmptyWrap>
  );
}

export default function ComplaintAnalytics({
  summary,
  categoryData = [],
  propertyData = [],
  tenantData = [],
  ageData = [],
  teamData = [],
}) {
  // ---- Transform API data to chart-friendly shapes ----
  const categories = useMemo(
    () =>
      (categoryData || [])
        .map((c) => ({ label: c.title, value: Number(c.count) || 0 }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 8), // top 8 for readability
    [categoryData]
  );

  const properties = useMemo(
    () =>
      (propertyData || [])
        .map((p) => ({ label: p.name, value: Number(p.count) || 0 }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 5),
    [propertyData]
  );

  const tenants = useMemo(
    () =>
      (tenantData || [])
        .map((t) => ({ label: t.name, value: Number(t.count) || 0 }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 5),
    [tenantData]
  );

  const ageing = useMemo(
    () =>
      (ageData || []).map((a) => ({
        label: a.ageBracket,
        value: Number(a.count) || 0,
      })),
    [ageData]
  );

  const team = useMemo(
    () =>
      (teamData || [])
        .map((s) => ({
          label: s.staffName,
          assigned: Number(s.assigned) || 0,
          resolved: Number(s.resolved) || 0,
          pending: Number(s.pending) || 0,
        }))
        .sort((a, b) => b.assigned - a.assigned)
        .slice(0, 8),
    [teamData]
  );

  // ---- Chart options ----
  const categoryOptions = {
    ...baseOptions,
    scales: {
      ...baseOptions.scales,
      x: {
        ...baseOptions.scales.x,
        ticks: { ...baseOptions.scales.x.ticks, precision: 0 },
      },
    },
  };

  const ageingOptions = {
    ...baseOptions,
    indexAxis: "x",
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { color: "#64748B", font: { size: 10 } },
      },
      y: {
        beginAtZero: true,
        grid: { color: "#F1F5F9" },
        border: { display: false },
        ticks: { precision: 0, color: "#64748B" },
      },
    },
  };

  const teamOptions = {
    ...baseOptions,
    indexAxis: "x",
    plugins: {
      ...baseOptions.plugins,
      legend: {
        display: true,
        position: "bottom",
        labels: { boxWidth: 10, usePointStyle: true },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { color: "#64748B", font: { size: 10 } },
      },
      y: {
        beginAtZero: true,
        grid: { color: "#F1F5F9" },
        border: { display: false },
        ticks: { precision: 0, color: "#64748B" },
      },
    },
  };

  const teamChartData = {
    labels: team.map((item) => item.label),
    datasets: [
      {
        label: "Assigned",
        data: team.map((item) => item.assigned),
        backgroundColor: COLORS.slate,
        borderRadius: 4,
      },
      {
        label: "Resolved",
        data: team.map((item) => item.resolved),
        backgroundColor: COLORS.green,
        borderRadius: 4,
      },
      {
        label: "Pending",
        data: team.map((item) => item.pending),
        backgroundColor: COLORS.orange,
        borderRadius: 4,
      },
    ],
  };

  return (
    <Dashboard>
      {/* <SectionHeading>Complaint Analytics</SectionHeading>
      <SectionDescription>
        Monitor complaint categories, properties, tenants and maintenance team
        performance.
      </SectionDescription> */}

      <Grid>
        <ChartCard
          title="Category-wise Complaints"
          subtitle="Complaints by issue type"
        >
          {categories.length ? (
            <Bar
              data={createBarData(categories, COLORS.indigo)}
              options={categoryOptions}
            />
          ) : (
            <EmptyState />
          )}
        </ChartCard>

        <ChartCard
          title="Properties with Most Complaints"
          subtitle="Top 5 properties by complaint volume"
        >
          {properties.length ? (
            <Bar
              data={createBarData(properties, COLORS.orange)}
              options={categoryOptions}
            />
          ) : (
            <EmptyState />
          )}
        </ChartCard>

        <ChartCard
          title="Tenants with Most Complaints"
          subtitle="Top 5 tenants by complaints raised"
        >
          {tenants.length ? (
            <Bar
              data={createBarData(tenants, COLORS.purple)}
              options={categoryOptions}
            />
          ) : (
            <EmptyState />
          )}
        </ChartCard>

        <ChartCard
          title="Ageing Distribution"
          subtitle="Unresolved complaints by age"
        >
          {ageing.length ? (
            <Bar
              data={createBarData(ageing, COLORS.red)}
              options={ageingOptions}
            />
          ) : (
            <EmptyState />
          )}
        </ChartCard>

        <ChartCard
          title="Team Performance"
          subtitle="Assigned, resolved and pending complaints"
        >
          {team.length ? (
            <Bar data={teamChartData} options={teamOptions} />
          ) : (
            <EmptyState />
          )}
        </ChartCard>

        {/* Optional: keep summary card in place of "Response Time" */}
        <ChartCard
          title="Summary Snapshot"
          subtitle="Overall complaint status"
        >
          <SummaryList>
            <SummaryRow>
              <span>Total</span>
              <strong>{summary?.total ?? 0}</strong>
            </SummaryRow>
            <SummaryRow>
              <span>Unassigned</span>
              <strong>{summary?.unassigned ?? 0}</strong>
            </SummaryRow>
            <SummaryRow>
              <span>Assigned</span>
              <strong>{summary?.assigned ?? 0}</strong>
            </SummaryRow>
            <SummaryRow>
              <span>Resolved</span>
              <strong>{summary?.resolved ?? 0}</strong>
            </SummaryRow>
            <SummaryRow>
              <span>Reopened</span>
              <strong>{summary?.reopened ?? 0}</strong>
            </SummaryRow>
            <SummaryRow>
              <span>Avg. Resolution</span>
              <strong>
                {summary?.avgResolutionHours
                  ? `${Number(summary.avgResolutionHours).toFixed(1)} hrs`
                  : "—"}
              </strong>
            </SummaryRow>
          </SummaryList>
        </ChartCard>
      </Grid>
    </Dashboard>
  );
}

// ---------- Styled components ----------
const Dashboard = tw.div`mt-6 pb-8`;

const SectionHeading = tw.h2`text-lg font-semibold text-gray-800`;

const SectionDescription = tw.p`mt-1 mb-5 text-sm text-gray-500`;

const Grid = tw.div`
  grid grid-cols-1 lg:grid-cols-2 gap-5
`;

const Card = tw.div`
  min-w-0 overflow-hidden
  rounded-lg bg-white
  border border-gray-100 shadow-sm
`;

const CardHeader = tw.div`
  min-h-[62px] px-4 py-3
  bg-white border-b border-gray-100
`;

const CardTitle = tw.h3`text-sm font-semibold text-gray-800`;

const CardSubtitle = tw.p`mt-1 text-xs text-gray-500`;

const ChartBody = tw.div`h-[280px] p-4`;

const EmptyWrap = tw.div`
  h-full flex items-center justify-center
  text-sm text-gray-400
`;

const SummaryList = tw.div`h-full flex flex-col justify-center gap-3`;

const SummaryRow = tw.div`
  flex items-center justify-between
  text-sm text-gray-600
  border-b border-dashed border-gray-100 pb-2
  last:border-none
`;

